import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Package, Plus, Edit, Trash2, ScanBarcode } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/shared/PageHeader";
import { EmptyState } from "../components/shared/EmptyState";
import { SearchFilterBar } from "../components/shared/SearchFilterBar";
import { BarcodeScannerModal } from "../components/shared/BarcodeScannerModal";
import { ExportButton } from "../components/shared/ExportButton";

const INITIAL_FORM = { sap_number: "", description: "", machine_number: "", manufacturer: "", manufacturer_part_number: "", vendor: "", vendor_part_number: "" };

function PartFormDialog({ open, onOpenChange, editing, formData, setFormData, onSubmit, onReset, machines }) {
  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) onReset(); }}>
      <DialogTrigger asChild>
        <Button className="bg-[#2563EB] hover:bg-[#0F172A] text-white rounded-none tracking-wide" data-testid="add-part-button">
          <Plus size={16} className="mr-2" />ADD PART
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white rounded-none border border-[#E2E8F0]" aria-describedby="part-form-desc">
        <DialogHeader><DialogTitle className="font-heading font-bold text-[#0F172A]">{editing ? "Edit Part" : "Add Part"}</DialogTitle></DialogHeader>
        <div id="part-form-desc" className="sr-only">Form to manage parts catalog</div>
        <form onSubmit={onSubmit} className="space-y-4">
          <div><Label className="text-[#0F172A]">SAP Number *</Label><Input value={formData.sap_number} onChange={(e) => setFormData({ ...formData, sap_number: e.target.value })} required className="mt-1 rounded-none border-[#E2E8F0] font-mono" data-testid="sap-number-input" /></div>
          <div><Label className="text-[#0F172A]">Description *</Label><Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required className="mt-1 rounded-none border-[#E2E8F0]" data-testid="part-description-input" /></div>
          <div>
            <Label className="text-[#0F172A]">Machine Number</Label>
            <select
              value={formData.machine_number}
              onChange={(e) => setFormData({ ...formData, machine_number: e.target.value })}
              className="mt-1 w-full h-10 px-3 border border-[#E2E8F0] bg-white text-sm font-mono text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
              data-testid="part-machine-number-select"
            >
              <option value="">— No machine assigned —</option>
              {machines.map((m) => (
                <option key={m.id} value={m.machine_number}>{m.machine_number} — {m.name}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-[#0F172A]">Manufacturer</Label><Input value={formData.manufacturer} onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })} className="mt-1 rounded-none border-[#E2E8F0]" data-testid="manufacturer-input" /></div>
            <div><Label className="text-[#0F172A]">Mfg Part #</Label><Input value={formData.manufacturer_part_number} onChange={(e) => setFormData({ ...formData, manufacturer_part_number: e.target.value })} className="mt-1 rounded-none border-[#E2E8F0] font-mono" data-testid="mfg-part-number-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-[#0F172A]">Vendor</Label><Input value={formData.vendor} onChange={(e) => setFormData({ ...formData, vendor: e.target.value })} className="mt-1 rounded-none border-[#E2E8F0]" data-testid="vendor-input" /></div>
            <div><Label className="text-[#0F172A]">Vendor Part #</Label><Input value={formData.vendor_part_number} onChange={(e) => setFormData({ ...formData, vendor_part_number: e.target.value })} className="mt-1 rounded-none border-[#E2E8F0] font-mono" data-testid="vendor-part-number-input" /></div>
          </div>
          <Button type="submit" className="w-full bg-[#2563EB] hover:bg-[#0F172A] text-white rounded-none" data-testid="submit-part-button">{editing ? "UPDATE" : "CREATE"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function PartsPage({ onLogout, userRole, isFeatureEnabled }) {
  const [parts, setParts] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [search, setSearch] = useState("");
  const [scannerOpen, setScannerOpen] = useState(false);

  const handleBarcodeScan = async (code) => {
    try {
      const resp = await axios.get(`${API}/parts/barcode-lookup?code=${encodeURIComponent(code)}`, { headers: getAuthHeaders() });
      toast.success(`Found: ${resp.data.sap_number} — ${resp.data.description}`);
      setSearch(resp.data.sap_number);
    } catch {
      toast.error(`No part found for code: ${code}`);
      setSearch(code);
    }
  };

  const fetchParts = useCallback(async () => {
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : "";
      const response = await axios.get(`${API}/parts${params}`, { headers: getAuthHeaders() });
      setParts(response.data);
    } catch { toast.error("Failed to load parts"); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => {
    axios.get(`${API}/machines`, { headers: getAuthHeaders() })
      .then((res) => setMachines(res.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const debounce = setTimeout(() => fetchParts(), 300);
    return () => clearTimeout(debounce);
  }, [fetchParts]);

  const resetForm = () => { setFormData(INITIAL_FORM); setEditing(null); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(`${API}/parts/${editing.id}`, formData, { headers: getAuthHeaders() });
        toast.success("Part updated");
      } else {
        await axios.post(`${API}/parts`, formData, { headers: getAuthHeaders() });
        toast.success("Part created");
      }
      setIsDialogOpen(false);
      resetForm();
      fetchParts();
    } catch (error) {
      toast.error(error.response?.data?.detail || (editing ? "Update failed" : "Creation failed"));
    }
  };

  const handleEdit = (part) => {
    setEditing(part);
    setFormData({
      sap_number: part.sap_number,
      description: part.description,
      machine_number: part.machine_number || "",
      manufacturer: part.manufacturer || "",
      manufacturer_part_number: part.manufacturer_part_number || "",
      vendor: part.vendor || "",
      vendor_part_number: part.vendor_part_number || "",
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this part?")) return;
    try {
      await axios.delete(`${API}/parts/${id}`, { headers: getAuthHeaders() });
      toast.success("Part deleted");
      fetchParts();
    } catch { toast.error("Delete failed"); }
  };

  if (loading) {
    return <Layout onLogout={onLogout} userRole={userRole}><div className="flex items-center justify-center h-64"><div className="text-[#64748B] font-mono">Loading parts catalog...</div></div></Layout>;
  }

  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-4" data-testid="parts-page">
        <PageHeader title="Parts Catalog" subtitle="Manage parts referenced in work orders" testId="parts-title">
          <div className="flex gap-2">
            <ExportButton endpoint="/export/parts" filename="parts_export.csv" />
            <Button
              variant="outline"
              onClick={() => setScannerOpen(true)}
              className="rounded-none border-[#2563EB] text-[#2563EB] hover:bg-[#2563EB] hover:text-white tracking-wide"
              data-testid="scan-barcode-button"
            >
              <ScanBarcode size={16} className="mr-2" />SCAN BARCODE
            </Button>
            <PartFormDialog open={isDialogOpen} onOpenChange={setIsDialogOpen} editing={editing} formData={formData} setFormData={setFormData} onSubmit={handleSubmit} onReset={resetForm} machines={machines} />
          </div>
        </PageHeader>

        <SearchFilterBar search={search} onSearchChange={setSearch} onClear={() => setSearch("")} />

        {parts.length === 0 ? (
          <EmptyState icon={Package} message="No parts found. Add your first part to the catalog." />
        ) : (
          <div className="bg-white border border-[#E2E8F0]">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="parts-table">
                <thead className="bg-[#FAFAFA] border-b border-[#E2E8F0]">
                  <tr>
                    {["SAP #", "Description", "Machine #", "Manufacturer", "Mfg Part #", "Vendor", "Vendor Part #", "Actions"].map((h, i) => (
                      <th key={h} className={`px-4 py-3 text-xs font-bold text-[#0F172A] tracking-wider uppercase ${i === 7 ? "text-right" : "text-left"}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]">
                  {parts.map((part) => (
                    <tr key={part.id} className="hover:bg-[#FAFAFA] transition-colors" data-testid={`part-row-${part.id}`}>
                      <td className="px-4 py-3 text-sm font-mono font-bold text-[#2563EB]">{part.sap_number}</td>
                      <td className="px-4 py-3 text-sm text-[#0F172A]">{part.description}</td>
                      <td className="px-4 py-3 text-sm font-mono text-[#0F172A] font-semibold" data-testid={`part-machine-${part.id}`}>{part.machine_number || "-"}</td>
                      <td className="px-4 py-3 text-sm text-[#64748B]">{part.manufacturer || "-"}</td>
                      <td className="px-4 py-3 text-sm font-mono text-[#64748B]">{part.manufacturer_part_number || "-"}</td>
                      <td className="px-4 py-3 text-sm text-[#64748B]">{part.vendor || "-"}</td>
                      <td className="px-4 py-3 text-sm font-mono text-[#64748B]">{part.vendor_part_number || "-"}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button size="sm" variant="outline" onClick={() => handleEdit(part)} className="rounded-none border-[#E2E8F0]" data-testid={`edit-part-${part.id}`}><Edit size={14} /></Button>
                          <Button size="sm" variant="outline" onClick={() => handleDelete(part.id)} className="rounded-none border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white" data-testid={`delete-part-${part.id}`}><Trash2 size={14} /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <BarcodeScannerModal open={scannerOpen} onClose={setScannerOpen} onScan={handleBarcodeScan} />
    </Layout>
  );
}
